import { useEffect, useState } from "react";
import {
    BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
    LineChart, Line, PieChart, Pie, Cell, Legend,
} from "recharts";
import {
    Award, TrendingUp, ShieldCheck, Users, ClipboardCheck, MessageSquareWarning,
    Download, Calendar, Snowflake, Repeat, MapPin,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, API } from "@/lib/api";

const COLORS = ["#E8873C", "#0A0A0A", "#8A8A8A", "#E2A878", "#D9772C"];

function Kpi({ icon: Icon, value, suffix, label, sub, testId }) {
    return (
        <div className="rounded-2xl border border-brand-line bg-white p-5 shadow-premium" data-testid={testId}>
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-brand-smoke">
                <Icon className="h-3.5 w-3.5 text-brand-orange" /> {label}
            </div>
            <div className="mt-3 font-display text-4xl text-brand-ink">
                {value ?? "—"}
                {suffix && value != null && <span className="text-xl text-brand-orange">{suffix}</span>}
            </div>
            {sub && <div className="mt-1 text-xs text-brand-smoke">{sub}</div>}
        </div>
    );
}

function downloadBilan(year) {
    const token = localStorage.getItem("cursus_physio_token");
    fetch(`${API}/admin/qualiopi/export-bilan.pdf?year=${year}`, {
        headers: { Authorization: `Bearer ${token}` },
    })
        .then((r) => r.blob())
        .then((blob) => {
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url; a.download = `bilan-pedagogique-${year}.pdf`; a.click();
            URL.revokeObjectURL(url);
            toast.success("Bilan annuel téléchargé");
        })
        .catch(() => toast.error("Téléchargement impossible"));
}

export default function AdminQualiopi() {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [year, setYear] = useState(new Date().getFullYear());

    useEffect(() => {
        api.get("/admin/qualiopi/stats")
            .then((r) => setStats(r.data))
            .catch((err) => toast.error(formatApiError(err)))
            .finally(() => setLoading(false));
    }, []);

    if (loading) {
        return <div className="mx-auto max-w-7xl px-6 py-16 text-center text-brand-smoke">Chargement des indicateurs Qualiopi…</div>;
    }
    const k = stats?.kpis || {};
    const c = stats?.charts || {};

    return (
        <div className="mx-auto max-w-7xl px-6 py-10" data-testid="page-admin-qualiopi">
            <div className="flex items-start justify-between gap-6">
                <div>
                    <div className="text-[11px] font-semibold uppercase tracking-[0.25em] text-brand-orange">
                        Démarche qualité Qualiopi
                    </div>
                    <h1 className="mt-2 font-display text-5xl font-normal tracking-tightest">Tableau de bord Qualiopi</h1>
                    <p className="mt-3 max-w-2xl text-brand-smoke">
                        Indicateurs en temps réel pour le pilotage qualité et la préparation des audits annuels (Centre Axiome).
                        Mise à jour à chaque réponse stagiaire.
                    </p>
                </div>
                <div className="flex items-center gap-3">
                    <select
                        value={year}
                        onChange={(e) => setYear(Number(e.target.value))}
                        data-testid="qualiopi-year-select"
                        className="rounded-full border border-brand-line bg-white px-4 py-2 text-sm outline-none focus:border-brand-orange"
                    >
                        {[2025, 2026, 2027].map((y) => <option key={y} value={y}>{y}</option>)}
                    </select>
                    <button
                        onClick={() => downloadBilan(year)}
                        data-testid="qualiopi-export-bilan"
                        className="inline-flex items-center gap-2 rounded-full bg-brand-orange px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#d9772c]"
                    >
                        <Download className="h-3.5 w-3.5" /> Bilan annuel PDF
                    </button>
                </div>
            </div>

            {/* KPI grid */}
            <div className="mt-8 grid grid-cols-2 gap-4 md:grid-cols-4" data-testid="qualiopi-kpis">
                <Kpi icon={Award} value={k.satisfaction_avg_5} suffix="/5" label="Satisfaction" sub={`${k.satisfaction_responses ?? 0} retours`} testId="kpi-satisfaction" />
                <Kpi icon={TrendingUp} value={k.nps} label="Net Promoter Score" sub={`${k.nps_responses ?? 0} réponses NPS`} testId="kpi-nps" />
                <Kpi icon={ShieldCheck} value={k.success_rate_pct} suffix="%" label="Taux de réussite" sub={`${k.post_acquis_responses ?? 0} évaluations post-acquis`} testId="kpi-success" />
                <Kpi icon={ClipboardCheck} value={k.completion_rate_pct} suffix="%" label="Taux de complétion" sub={`${k.completion_sessions_counted ?? 0} sessions évaluées`} testId="kpi-completion" />
                <Kpi icon={Users} value={k.stagiaires_formed} label="Stagiaires formés" testId="kpi-stagiaires" />
                <Kpi icon={Calendar} value={k.sessions_done} label="Sessions réalisées" testId="kpi-sessions" />
                <Kpi icon={MessageSquareWarning} value={k.reclamations_total} label="Réclamations" sub={`${k.reclamations_resolved ?? 0} résolues`} testId="kpi-reclamations" />
                <Kpi icon={ShieldCheck} value={k.reclamations_resolution_rate_pct} suffix="%" label="Résolution réclamations" testId="kpi-resolution" />
                <Kpi icon={Snowflake} value={k.satisfaction_froid_avg_5} suffix="/5" label="Satisfaction à froid" sub={`${k.satisfaction_froid_responses ?? 0} retours · J+30`} testId="kpi-froid" />
                <Kpi icon={Repeat} value={k.transfert_rate_pct} suffix="%" label="Transfert des acquis" sub={`${k.transfert_responses ?? 0} retours · J+90`} testId="kpi-transfert" />
                <Kpi
                    icon={MapPin}
                    value={`${k.priority_coverage_count ?? 0}/${k.priority_coverage_total ?? 9}`}
                    label="Couverture villes cibles"
                    sub="Sessions à venir dans les 9 villes prioritaires"
                    testId="kpi-priority-coverage"
                />
            </div>

            {(c.priority_coverage || []).length > 0 && (
                <div className="mt-8 rounded-2xl border border-brand-line bg-white p-6 shadow-premium" data-testid="priority-coverage-grid">
                    <div className="flex items-center justify-between gap-3">
                        <div>
                            <h3 className="font-display text-xl">Couverture des 9 villes cibles</h3>
                            <p className="mt-1 text-xs text-brand-smoke">Une ville est marquée "couverte" si au moins une session est planifiée à venir.</p>
                        </div>
                    </div>
                    <div className="mt-5 grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-5 lg:grid-cols-9">
                        {(c.priority_coverage || []).map((row) => (
                            <div
                                key={row.city}
                                data-testid={`priority-city-${row.city.toLowerCase()}`}
                                className={`rounded-xl border p-3 text-center transition ${
                                    row.covered ? "border-brand-orange bg-brand-orange/10" : "border-brand-line bg-[#FAFAFA] opacity-70"
                                }`}
                            >
                                <MapPin className={`mx-auto h-4 w-4 ${row.covered ? "text-brand-orange" : "text-brand-smoke"}`} />
                                <div className="mt-2 text-xs font-semibold">{row.city}</div>
                                <div className={`mt-1 text-[10px] ${row.covered ? "text-brand-orange" : "text-brand-smoke"}`}>
                                    {row.covered ? "✓ Couverte" : "À planifier"}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Charts */}
            <div className="mt-10 grid gap-5 lg:grid-cols-2">
                <div className="rounded-2xl border border-brand-line bg-white p-6 shadow-premium" data-testid="chart-formation-satisfaction">
                    <h3 className="font-display text-xl">Satisfaction par formation</h3>
                    <p className="text-xs text-brand-smoke">Note moyenne /100 par formation, sur la base des questionnaires à chaud.</p>
                    {(c.formation_satisfaction || []).length === 0 ? (
                        <div className="mt-8 py-10 text-center text-sm text-brand-smoke">Aucune donnée.</div>
                    ) : (
                        <ResponsiveContainer width="100%" height={280}>
                            <BarChart data={c.formation_satisfaction.slice(0, 8)} margin={{ top: 20, right: 16, left: -8, bottom: 50 }}>
                                <CartesianGrid stroke="#E7E3DC" vertical={false} />
                                <XAxis dataKey="formation_slug" angle={-25} textAnchor="end" height={60} fontSize={10} stroke="#8A8A8A" />
                                <YAxis fontSize={10} stroke="#8A8A8A" />
                                <Tooltip />
                                <Bar dataKey="avg_pct" fill="#E8873C" radius={[6, 6, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    )}
                </div>

                <div className="rounded-2xl border border-brand-line bg-white p-6 shadow-premium" data-testid="chart-monthly-trend">
                    <h3 className="font-display text-xl">Évolution mensuelle</h3>
                    <p className="text-xs text-brand-smoke">Satisfaction moyenne sur les 12 derniers mois.</p>
                    {(c.monthly_trend || []).length === 0 ? (
                        <div className="mt-8 py-10 text-center text-sm text-brand-smoke">Pas encore de données mensuelles.</div>
                    ) : (
                        <ResponsiveContainer width="100%" height={280}>
                            <LineChart data={c.monthly_trend} margin={{ top: 20, right: 16, left: -8, bottom: 20 }}>
                                <CartesianGrid stroke="#E7E3DC" vertical={false} />
                                <XAxis dataKey="month" fontSize={10} stroke="#8A8A8A" />
                                <YAxis fontSize={10} stroke="#8A8A8A" domain={[0, 100]} />
                                <Tooltip />
                                <Line type="monotone" dataKey="avg_pct" stroke="#E8873C" strokeWidth={2.5} dot={{ fill: "#E8873C", r: 4 }} />
                            </LineChart>
                        </ResponsiveContainer>
                    )}
                </div>

                <div className="rounded-2xl border border-brand-line bg-white p-6 shadow-premium lg:col-span-2" data-testid="chart-followup">
                    <h3 className="font-display text-xl">Niveau d'application : à chaud vs J+30 vs J+90</h3>
                    <p className="text-xs text-brand-smoke">
                        Comparaison des 3 paliers de mesure Qualiopi : satisfaction à chaud (fin de session), satisfaction à froid (J+30), transfert des acquis (J+90, % à ≥4/5).
                    </p>
                    {(c.follow_up_comparison || []).every((d) => d.count === 0) ? (
                        <div className="mt-8 py-10 text-center text-sm text-brand-smoke">
                            Aucune réponse aux nouveaux paliers de mesure pour le moment. Les emails J+30 et J+90 sont déclenchés automatiquement par le scheduler quotidien.
                        </div>
                    ) : (
                        <ResponsiveContainer width="100%" height={260}>
                            <BarChart data={c.follow_up_comparison} margin={{ top: 20, right: 16, left: -8, bottom: 20 }}>
                                <CartesianGrid stroke="#E7E3DC" vertical={false} />
                                <XAxis dataKey="phase" fontSize={11} stroke="#8A8A8A" />
                                <YAxis fontSize={10} stroke="#8A8A8A" domain={[0, 100]} />
                                <Tooltip formatter={(v, name) => [`${v}%`, name === "avg_pct" ? "Score moyen" : name]} />
                                <Bar dataKey="avg_pct" fill="#E8873C" radius={[6, 6, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    )}
                </div>

                <div className="rounded-2xl border border-brand-line bg-white p-6 shadow-premium lg:col-span-2" data-testid="chart-reclamations">
                    <h3 className="font-display text-xl">Répartition des réclamations</h3>
                    <p className="text-xs text-brand-smoke">Par typologie, depuis le démarrage de la plateforme.</p>
                    {(c.reclamations_by_type || []).length === 0 ? (
                        <div className="mt-8 py-10 text-center text-sm text-brand-smoke">Aucune réclamation à ce jour.</div>
                    ) : (
                        <ResponsiveContainer width="100%" height={260}>
                            <PieChart>
                                <Pie data={c.reclamations_by_type} dataKey="count" nameKey="type" cx="50%" cy="50%" outerRadius={90} innerRadius={40} label>
                                    {(c.reclamations_by_type || []).map((_, i) => (
                                        <Cell key={i} fill={COLORS[i % COLORS.length]} />
                                    ))}
                                </Pie>
                                <Tooltip />
                                <Legend />
                            </PieChart>
                        </ResponsiveContainer>
                    )}
                </div>
            </div>
        </div>
    );
}
